// ============================================================================
//  Sélecteurs CRM avec recherche serveur (entreprise, contact, commercial).
//  S'appuient sur AsyncCombobox : plus de liste tronquée à une page de 100.
// ============================================================================
import { AsyncCombobox, type AsyncComboboxProps } from '@/components/ui/AsyncCombobox'
import { crmApi, type CrmCompany, type CrmContact } from '@/modules/crm/api/crm.api'
import { hrApi, type Employee } from '@/modules/hr/api/hr.api'

type WrapperProps<T> = Omit<
  AsyncComboboxProps<T>,
  'fetchOptions' | 'getOptionValue' | 'getOptionLabel' | 'renderOption' | 'queryKey'
>

const fullName = (p: { firstName?: string; lastName?: string }) =>
  [p.firstName, p.lastName].filter(Boolean).join(' ')

// Enveloppe paginée du backend CRM : { items, meta: { total, … } }
const fromCrmPage = <T,>(d: any): { items: T[]; total?: number } => ({
  items: d?.items ?? [],
  total: d?.meta?.total,
})

export function CompanyCombobox(props: WrapperProps<CrmCompany>) {
  return (
    <AsyncCombobox<CrmCompany>
      placeholder="— Aucune —"
      emptyText="Aucune entreprise"
      {...props}
      queryKey="crm-companies"
      fetchOptions={(search, limit) =>
        crmApi.listCompanies({ search: search || undefined, limit }).then(fromCrmPage<CrmCompany>)
      }
      getOptionValue={(c) => c.id}
      getOptionLabel={(c) => c.name}
      renderOption={(c) => [c.city, c.industry].filter(Boolean).join(' · ') || null}
    />
  )
}

export function ContactCombobox(props: WrapperProps<CrmContact>) {
  return (
    <AsyncCombobox<CrmContact>
      placeholder="— Aucun —"
      emptyText="Aucun contact"
      {...props}
      queryKey="crm-contacts"
      fetchOptions={(search, limit) =>
        crmApi.listContacts({ search: search || undefined, limit }).then(fromCrmPage<CrmContact>)
      }
      getOptionValue={(c) => c.id}
      getOptionLabel={fullName}
      renderOption={(c) => c.company?.name ?? c.email ?? null}
    />
  )
}

/** Employés actifs — nécessite la permission `hr:view` côté API. */
export function EmployeeCombobox(props: WrapperProps<Employee>) {
  return (
    <AsyncCombobox<Employee>
      placeholder="Sélectionner…"
      emptyText="Aucun employé actif"
      {...props}
      queryKey="employees"
      fetchOptions={(search, limit) =>
        hrApi.employees
          .list({ search: search || undefined, limit, status: 'ACTIVE' })
          .then((d: any) => ({ items: (d?.employees ?? []) as Employee[], total: d?.total }))
      }
      getOptionValue={(e) => e.id}
      getOptionLabel={fullName}
      renderOption={(e) => e.jobTitle || e.matricule || null}
    />
  )
}
